'use client'

import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import {
  ICartItem,
  IModifierQuantityObj,
  IRedux,
} from '@/app/[locale]/_store/types'
import {
  ISavedModifier,
  IProduct,
  IProductVariant,
} from '@/app/[locale]/_types'
import {
  onDecreaseModifierQuantity,
  onDecreaseModifierVariantQuantity,
  onGroupModifierChange,
  onIncreaseModifierQuantity,
  onIncreaseModifierVariantQuantity,
  onModifierChange,
} from '@/app/[locale]/_utils/modifier/actions'
import ModifierCheckbox from '@/app/[locale]/_components/ModifierCheckbox'
import { getProductModifier } from '@/app/[locale]/_service/product.service'
import {
  buildProductModifierRequestParams,
  buildProductModifierState,
  getOrderingDeliveryType,
  hasInvisibleGroupModifier,
  MODIFIERS_MIN_HEIGHT,
} from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { useSelector } from 'react-redux'
import { usePathname } from '@/i18n/navigation'

interface IProps {
  product?: IProduct | IProductVariant
  quantity: number
  productInCart: ICartItem | null
  expectedModifiers: string[]
  orderModifiers: ISavedModifier[]
  modifiersQuantity: IModifierQuantityObj[]
  setOrderModifiers: React.Dispatch<React.SetStateAction<ISavedModifier[]>>
  setModifiersQuantity: React.Dispatch<
    React.SetStateAction<IModifierQuantityObj[]>
  >
  onSaveCompulsories: (e: any[]) => void
  scrollToExpectedModifierRef?: any
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

function Modifiers({
  product,
  quantity,
  productInCart,
  orderModifiers,
  modifiersQuantity,
  expectedModifiers,
  setOrderModifiers,
  setModifiersQuantity,
  onSaveCompulsories,
  scrollToExpectedModifierRef,
  setLoading,
}: IProps) {
  const pathname = usePathname()
  const { branch } = useSelector((state: IRedux) => state.common)
  const { is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const isHall = is_hall === 'true'

  const isMenuActive =
    isHall || pathname.endsWith('/menu')
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || ''

  const deliveryType = getOrderingDeliveryType({
    isHall,
    orderType: order_type?.type,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['modifier', product?.id, isMenuActive, deliveryType],
    queryFn: () =>
      getProductModifier(
        buildProductModifierRequestParams({
          productId: product?.id || '',
          menuId: isMenuActive,
          deliveryType,
        })
      ).then((res) => res?.data),
    enabled: Boolean(product?.has_modifier),
  })

  const currentLocale = useCustomLocale()
  const t = useTranslations()

  useEffect(() => {
    if (product?.has_modifier) {
      setLoading(isLoading)
    } else if (product?.id) {
      setLoading(false)
    }
  }, [isLoading, product?.has_modifier])

  // Get modifiers by variant
  useEffect(() => {
    if (!data) return

    const { modifiers, groupAmounts, compulsoryModifiers } =
      buildProductModifierState(
        data.product_modifiers,
        isMenuActive || undefined
      )

    setOrderModifiers(modifiers)
    setModifiersQuantity(groupAmounts)
    onSaveCompulsories(compulsoryModifiers)
  }, [data, isMenuActive])

  const modifiersMinHeight = hasInvisibleGroupModifier(data?.product_modifiers)
    ? undefined
    : MODIFIERS_MIN_HEIGHT

  const modifierQuantityHandler = (modifierId: string) => {
    const element = orderModifiers.find(
      (item: any) => item.modifier_id === modifierId
    )

    return element?.modifier_quantity ?? 0
  }
  const checkModifierHandler = (modifierId: string) => {
    const isChecked = orderModifiers.some(
      (item) => item.modifier_id === modifierId && item.modifier_quantity > 0
    )
    return isChecked ? isChecked : isOrderedModifierHandler(modifierId)
  }
  const isOrderedModifierHandler = (modifierId: string) => {
    if (
      productInCart?.order_modifiers &&
      productInCart?.order_modifiers?.length > 0
    ) {
      let isIncluded = false
      productInCart?.order_modifiers?.map((item) => {
        if (item.modifier_id == modifierId && item.modifier_quantity > 0) {
          isIncluded = true
        }
      })
      return isIncluded
    }
    return false
  }

  return (
    <Stack mb={4} gap={2} minH={modifiersMinHeight}>
      {data?.product_modifiers?.group_modifiers?.map((modifier, idx) => {
        if (modifier.is_visible === false) return null
        if (modifiersQuantity[idx]?.id !== modifier.id) return null

        return (
            <Box key={modifier.id + modifier.from_product_id + idx}>
              <HStack justify='space-between' my={2}>
                <Text
                  fontWeight={600}
                  lineHeight={'14px'}
                  ref={
                    expectedModifiers?.[0] === modifier.name[currentLocale]
                      ? scrollToExpectedModifierRef
                      : null
                  }
                  color={
                    expectedModifiers.includes(modifier.name[currentLocale])
                      ? 'red'
                      : 'text'
                  }
                >
                  {modifier.name?.[currentLocale]}
                </Text>
                <HStack>
                  <Text fontSize='sm' color='text.muted'>
                    {modifier?.min_amount ? (
                      <>
                        {t('at_least').substring(0, 3)}: {modifier?.min_amount}
                      </>
                    ) : (
                      t('optional')
                    )}
                  </Text>
                  <Text fontSize='sm' color='text.muted'>
                    {modifier?.max_amount ? (
                      <>
                        {t('maximum').substring(0, 4)}: {modifier?.max_amount}
                      </>
                    ) : (
                      t('optional')
                    )}
                  </Text>
                </HStack>
              </HStack>
              <SimpleGrid
                columns={{
                  base: modifier?.is_checkbox ? 2 : 1,
                  md: modifier?.is_checkbox ? 3 : 1,
                }}
                gap={3}
              >
                {modifier?.variants?.map((variant, index) => (
                  <Box key={variant.id + index}>
                    <ModifierCheckbox
                      data={variant}
                      inStop={Boolean(
                        isMenuActive &&
                          (!modifier.active_in_menu || !variant.active_in_menu)
                      )}
                      isCheckbox={modifier?.is_checkbox}
                      checked={checkModifierHandler(variant?.id)}
                      quantity={modifierQuantityHandler(variant?.id) * quantity}
                      onChange={({ target }) =>
                        onGroupModifierChange(
                          target.checked,
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity
                        )
                      }
                      label={variant.title?.[currentLocale]}
                      outPrice={variant.out_price}
                      decrease={() =>
                        onDecreaseModifierVariantQuantity(
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity
                        )
                      }
                      increase={() =>
                        onIncreaseModifierVariantQuantity(
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity
                        )
                      }
                    />
                    {modifier?.variants?.length > 2 &&
                      !modifier?.is_checkbox && (
                        <Box h={'2px'} bg={'#00000014'} />
                      )}
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
        )
      })}
      {data?.product_modifiers?.single_modifiers?.map((modifier, idx) => (
        <Box key={modifier.id + modifier.from_product_id + idx}>
          <HStack justify='space-between' my={2}>
            <Text
              fontWeight={600}
              lineHeight={'14px'}
              color={
                expectedModifiers.includes(modifier.name[currentLocale])
                  ? 'red'
                  : 'text'
              }
            >
              {modifier.name?.[currentLocale]}
            </Text>
            <HStack>
              <Text fontSize='sm' color='text.muted'>
                {modifier?.min_amount ? (
                  <>
                    {t('at_least').substring(0, 3)}: {modifier?.min_amount}
                  </>
                ) : (
                  t('optional')
                )}
              </Text>
              <Text fontSize='sm' color='text.muted'>
                {modifier?.max_amount ? (
                  <>
                    {t('maximum').substring(0, 3)}: {modifier?.max_amount}
                  </>
                ) : (
                  t('optional')
                )}
              </Text>
            </HStack>
          </HStack>
          <SimpleGrid
            columns={{
              base: modifier?.is_checkbox ? 2 : 1,
              md: modifier?.is_checkbox ? 3 : 1,
            }}
            gap={3}
          >
            <ModifierCheckbox
              data={modifier}
              inStop={Boolean(isMenuActive && !modifier.active_in_menu)}
              isCheckbox={modifier?.is_checkbox}
              checked={checkModifierHandler(modifier?.id)}
              quantity={modifierQuantityHandler(modifier?.id) * quantity}
              onChange={({ target }) =>
                onModifierChange(target.checked, modifier, setOrderModifiers)
              }
              label={modifier.name?.[currentLocale]}
              outPrice={modifier.price}
              isCompulsory={modifier.is_compulsory}
              decrease={() =>
                onDecreaseModifierQuantity(
                  modifier,
                  orderModifiers,
                  setOrderModifiers
                )
              }
              increase={() =>
                onIncreaseModifierQuantity(
                  modifier,
                  orderModifiers,
                  setOrderModifiers
                )
              }
            />
          </SimpleGrid>
        </Box>
      ))}
    </Stack>
  )
}

export default Modifiers
